/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useCallback, useRef, useState } from "react";
import { useNotificationContext } from "../contexts/notificationContext";
import { EXPORT_ERROR_MESSAGE, downloadExport } from "../utils/reportExport";

/**
 * The run half of a report export button: one in-flight request at a time, a success or
 * failure notification, and a boolean the caller can disable its control with.
 *
 * Resolves to whether the download happened, so the caller can keep the format dialog open
 * on failure — a scope that is too large or a busy renderer are both worth retrying from,
 * and closing the dialog would throw away the format the user just chose.
 *
 * The in-flight guard is a ref, not the state flag: two clicks in the same tick both read the
 * pre-render state, and a ref is already updated by the second one.
 */
export const useReportExport = () => {
  const showNotification = useNotificationContext();
  const [exporting, setExporting] = useState(false);
  const inFlight = useRef(false);

  const runExport = useCallback(
    async ({ url, fallbackFilename, format }) => {
      if (inFlight.current) return false;

      inFlight.current = true;
      setExporting(true);

      try {
        await downloadExport({ url, fallbackFilename });
        showNotification(`${String(format).toUpperCase()} downloaded successfully`, "success");
        return true;
      } catch (error) {
        showNotification(error?.message || EXPORT_ERROR_MESSAGE, "error");
        return false;
      } finally {
        inFlight.current = false;
        setExporting(false);
      }
    },
    [showNotification],
  );

  return { exporting, runExport };
};
